import { useEffect, useState } from 'react';
import { Flex } from 'antd';
import { ITransactionFilter } from '../../interface/transaction';
import { Dropdown } from '../Ant';
import FilterPopoverContent from './filterPopoverContent';
import { usePopupFocus } from '../../hooks/usePopupFocus';

interface FilterOption {
  label: string;
  value: string;
}

interface FilterPopoverProps {
  popoverTitle?: string;
  primarytitle?: string;
  secondarytitle?: string;
  ownerTitle?: string;
  ownerOptions?: FilterOption[];
  radioOptions?: FilterOption[];
  checkboxOptions?: FilterOption[];
  onOpenChange: (open: boolean) => void;
  onClear: () => void;
  visible: boolean;
  children: React.ReactNode;
  selectedValue?: ITransactionFilter[];
  setSelectedValue?: (val: ITransactionFilter) => void;
  setSelectedOwner?: (val: ITransactionFilter) => void;
  setCheckBoxValues?: (val: ITransactionFilter[]) => void;
  disabled?: boolean;
  disabledCheckboxValues?: string[];
  disabledRadioValues?: string[];
  disabledInfoMessage?: string;
  hasMoreCheckboxOptions?: boolean;
  isFetchingMoreCheckboxOptions?: boolean;
  onLoadMoreCheckboxOptions?: () => void;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  popoverTitle,
  disabled = false,
  primarytitle,
  secondarytitle,
  ownerTitle,
  ownerOptions = [],
  radioOptions = [],
  checkboxOptions = [],
  onOpenChange,
  onClear,
  visible,
  selectedValue,
  setSelectedValue,
  setSelectedOwner,
  setCheckBoxValues,
  children,
  disabledCheckboxValues = [],
  disabledRadioValues = [],
  disabledInfoMessage,
  hasMoreCheckboxOptions,
  isFetchingMoreCheckboxOptions,
  onLoadMoreCheckboxOptions,
}) => {
  const onClose = () => onOpenChange(false);
  const { contentRef, onKeyDown, onBlur, tabIndex } = usePopupFocus(visible, onClose);
  const [selectedRadio, setSelectedRadio] = useState<string | null>(null);
  const [tempSelectedValue, setTempSelectedValue] = useState<ITransactionFilter | null>(null);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
  const [selectedOwnerValue, setSelectedOwnerValue] = useState<string | null>(null);

  useEffect(() => {
    if (ownerOptions.length > 0) {
      const matchedOwner = selectedValue?.find((item) =>
        ownerOptions.some((option) => option.value === item.value),
      );
      setSelectedOwnerValue(matchedOwner?.value ?? null);
    }
    if (radioOptions.length > 0) {
      const matchedRadio = selectedValue?.find((item) =>
        radioOptions.some((option) => option.value === item.value),
      );
      setSelectedRadio(matchedRadio?.value ?? null);
      setTempSelectedValue(matchedRadio ?? null);
    }
    if (checkboxOptions?.length > 0) {
      const matchedCheckboxes = selectedValue?.filter((item) =>
        checkboxOptions.some((option) => option.value === item.value),
      );
      setSelectedCheckboxes(matchedCheckboxes?.map((item) => item.value) ?? []);
    }
  }, [selectedValue]);

  const renderPopoverContent = () => (
    <Flex
      vertical
      ref={contentRef}
      role="dialog"
      aria-label={popoverTitle}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      data-testid="filter-popover-focus"
    >
      <FilterPopoverContent
        popoverTitle={popoverTitle}
        primarytitle={primarytitle}
        secondarytitle={secondarytitle}
        ownerTitle={ownerTitle}
        ownerOptions={ownerOptions}
        radioOptions={radioOptions}
        checkboxOptions={checkboxOptions}
        onClose={onClose}
        onClear={onClear}
        selectedRadio={selectedRadio}
        setSelectedRadio={setSelectedRadio}
        selectedOwner={selectedOwnerValue}
        setSelectedOwner={setSelectedOwnerValue}
        setOwnerValue={setSelectedOwner}
        tempSelectedValue={tempSelectedValue}
        setTempSelectedValue={setTempSelectedValue}
        selectedCheckboxes={selectedCheckboxes}
        setSelectedCheckboxes={setSelectedCheckboxes}
        disabled={disabled}
        disabledCheckboxValues={disabledCheckboxValues}
        disabledRadioValues={disabledRadioValues}
        disabledInfoMessage={disabledInfoMessage}
        setCheckBoxValues={setCheckBoxValues}
        setSelectedValue={setSelectedValue}
        hasMoreCheckboxOptions={hasMoreCheckboxOptions}
        isFetchingMoreCheckboxOptions={isFetchingMoreCheckboxOptions}
        onLoadMoreCheckboxOptions={onLoadMoreCheckboxOptions}
      />
    </Flex>
  );

  return (
    <Dropdown
      overlayClassName="filter-dropdown"
      open={visible}
      popupRender={renderPopoverContent}
      getPopupContainer={() => document.body}
      trigger={['click']}
      placement="bottom"
      onOpenChange={onOpenChange}
    >
      {children}
    </Dropdown>
  );
};

export default FilterPopover;
